import { Link, useNavigate, useParams } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { historyApi } from '../api/historyContributions';
import { ApiError } from '../lib/api';
import { toast } from '../lib/toast';
import { eventDate } from '../lib/format';
import { ContributionForm } from '../components/ContributionForm';
import { ContributionStatusBadge } from '../components/ContributionStatusBadge';
import { PageSkeleton } from '../components/Skeleton';
import { useConfirm } from '../components/ui';

/** A member's own contribution: the form, and submit / withdraw / delete. */
export function ContributionEdit() {
  const { id = '' } = useParams();
  const navigate = useNavigate();
  const qc = useQueryClient();
  const confirm = useConfirm();
  const { data, isLoading, isError } = useQuery({ queryKey: ['contribution', id], queryFn: () => historyApi.get(id) });

  const refresh = () => qc.invalidateQueries({ queryKey: ['contribution', id] });
  const onErr = (e: unknown, fallback: string) => toast.error(e instanceof ApiError ? e.message : fallback);
  const submit = useMutation({
    mutationFn: () => historyApi.submit(id),
    onSuccess: () => {
      toast.success('Submitted — a Records Admin will review it');
      void refresh();
    },
    onError: (e) => onErr(e, 'Could not submit'),
  });
  const withdraw = useMutation({
    mutationFn: () => historyApi.withdraw(id),
    onSuccess: () => void refresh(),
    onError: (e) => onErr(e, 'Could not withdraw'),
  });
  const remove = useMutation({
    mutationFn: () => historyApi.remove(id),
    onSuccess: () => {
      toast.success('Contribution deleted');
      navigate('/contribute');
    },
    onError: (e) => onErr(e, 'Could not delete'),
  });

  if (isLoading) return <PageSkeleton lines={4} />;
  if (isError || !data) return <p className="text-wine-300">Contribution not found.</p>;
  const { contribution: c, files, linkedEvent, mergedEvent } = data;
  const readOnly = c.status === 'merged' || c.status === 'declined';
  const canSubmit = c.status === 'draft' || c.status === 'needs_info';

  return (
    <div className="space-y-5">
      <Link to="/contribute" className="text-xs text-cream-300/60 hover:text-gold-200">
        ← Your contributions
      </Link>
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <h1 className="font-display text-3xl text-cream-50">{c.payload.event?.title ?? 'Untitled contribution'}</h1>
          <p className="mt-1 text-sm text-cream-300/60">
            {linkedEvent ? (
              <>
                Adding to{' '}
                <Link to={`/archive/${linkedEvent.id}`} className="text-gold-200 hover:underline">
                  {linkedEvent.title}
                </Link>{' '}
                · {eventDate(linkedEvent)}
              </>
            ) : (
              'A tasting not yet in the archive (or one you\'re not sure about)'
            )}
          </p>
        </div>
        <ContributionStatusBadge status={c.status} />
      </div>

      {c.status === 'needs_info' && (
        <div className="rounded border border-gold-600/40 bg-gold-500/10 px-4 py-3 text-sm">
          <p className="text-gold-200">A Records Admin has a question:</p>
          <p className="mt-1 whitespace-pre-line text-cream-100">{c.reviewNotes ?? '(no note)'}</p>
          <p className="mt-2 text-xs text-cream-300/60">Add what you can below, then resubmit.</p>
        </div>
      )}
      {c.status === 'declined' && (
        <div className="rounded border border-wine-700/50 bg-wine-900/20 px-4 py-3 text-sm">
          <p className="text-wine-300">This contribution was not merged.</p>
          {c.reviewNotes && <p className="mt-1 whitespace-pre-line text-cream-100">{c.reviewNotes}</p>}
        </div>
      )}
      {c.status === 'merged' && mergedEvent && (
        <div className="rounded border border-gold-600/40 bg-gold-500/10 px-4 py-3 text-sm">
          <p className="text-gold-200">
            Merged into{' '}
            <Link to={`/archive/${mergedEvent.id}`} className="underline">
              {mergedEvent.title}
            </Link>{' '}
            · {eventDate(mergedEvent)}. Thank you.
          </p>
          {c.mergeSummary && (
            <p className="mt-1 text-xs text-cream-300/70">
              {c.mergeSummary.winesCreated + c.mergeSummary.winesUpdated} wines · {c.mergeSummary.scoresCreated + c.mergeSummary.scoresUpdated} scores ·{' '}
              {c.mergeSummary.mediaCreated} photos/scans · {c.mergeSummary.importBatchesCreated} spreadsheets to import
            </p>
          )}
        </div>
      )}
      {c.status === 'submitted' && (
        <p className="text-sm text-cream-300/70">Submitted and waiting for review. You can still edit it, or withdraw it to keep working privately.</p>
      )}

      <ContributionForm contributionId={c.id} initial={c.payload} files={files} readOnly={readOnly} />

      {!readOnly && (
        <div className="flex flex-wrap items-center gap-3 border-t border-ink-800 pt-4">
          {canSubmit && (
            <button className="btn-primary" onClick={() => submit.mutate()} disabled={submit.isPending}>
              {submit.isPending ? 'Submitting…' : c.status === 'needs_info' ? 'Resubmit for review' : 'Submit for review'}
            </button>
          )}
          {c.status === 'submitted' && (
            <button className="btn-ghost" onClick={() => withdraw.mutate()} disabled={withdraw.isPending}>
              Withdraw
            </button>
          )}
          {c.status === 'draft' && (
            <button
              className="text-sm text-wine-300 hover:underline"
              onClick={async () => {
                if (await confirm({ title: 'Delete this contribution?', description: 'Its files are removed too.', confirmLabel: 'Delete', destructive: true }))
                  remove.mutate();
              }}
            >
              Delete
            </button>
          )}
        </div>
      )}
    </div>
  );
}
